<script setup>
import AppIcon from './AppIcon.vue'

const cols = [
  {
    title: 'Pre-Bid',
    links: [
      { to: '/tender-insights', label: 'Tender Insights' },
      { to: '/bid-intelligence', label: 'Bid Intelligence' },
      { to: '/timeline', label: 'Timeline & Alerts' }
    ]
  },
  {
    title: 'Post-Bid',
    links: [
      { to: '/email-intelligence', label: 'Email Intelligence' },
      { to: '/claims', label: 'Claims Management' },
      { to: '/documents', label: 'Document Management' },
      { to: '/chat-assistant', label: 'Contract Chat Assistant' }
    ]
  },
  {
    title: 'Industries',
    links: [
      { to: '/', label: 'EPC & Construction' },
      { to: '/', label: 'Infrastructure & Railways' },
      { to: '/', label: 'Power & Water' },
      { to: '/', label: 'Government Contracting' }
    ]
  }
]
const year = new Date().getFullYear()
</script>

<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-cta card" v-reveal>
        <div>
          <h3>Ready to win more tenders?</h3>
          <p>See how BidSnap AI turns complex tender packages into submission-ready bids — in minutes, not weeks.</p>
        </div>
        <RouterLink to="/contact" class="btn btn-primary btn-lg">
          Book a Demo <AppIcon name="arrow" :size="18" class="arrow" />
        </RouterLink>
      </div>

      <div class="footer-grid">
        <div class="footer-brand">
          <RouterLink to="/" class="brand">
            <svg viewBox="0 0 64 64" width="32" height="32">
              <defs><linearGradient id="fg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#34f0c0"/><stop offset="1" stop-color="#0ea5e9"/></linearGradient></defs>
              <!-- BidSnap mark: snap-frame corners capturing a checkmark -->
              <g fill="none" stroke="url(#fg)" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M23 15h-4a4 4 0 0 0-4 4v4"/>
                <path d="M41 15h4a4 4 0 0 1 4 4v4"/>
                <path d="M23 49h-4a4 4 0 0 1-4-4v-4"/>
                <path d="M41 49h4a4 4 0 0 0 4-4v-4"/>
              </g>
              <path d="M23 33.5l6.5 6.5L44 25" fill="none" stroke="url(#fg)" stroke-width="7" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="brand-name">BidSnap<span class="brand-ai"> AI</span></span>
          </RouterLink>
          <p class="footer-tag">
            The AI bid & contract intelligence platform — from tender discovery to final submission, all in one streamlined workflow.
          </p>
          <div class="badges">
            <span class="badge"><AppIcon name="shield" :size="15" /> ISO 27001</span>
            <span class="badge"><AppIcon name="lock" :size="15" /> SOC 2</span>
            <span class="badge"><AppIcon name="globe" :size="15" /> GDPR</span>
          </div>
        </div>

        <div v-for="col in cols" :key="col.title" class="footer-col">
          <h4>{{ col.title }}</h4>
          <ul>
            <li v-for="l in col.links" :key="l.label">
              <RouterLink :to="l.to">{{ l.label }}</RouterLink>
            </li>
          </ul>
        </div>
      </div>

      <div class="footer-bottom">
        <p>© {{ year }} BidSnap AI. Crafted for bid, proposal & contract teams worldwide.</p>
        <div class="footer-legal">
          <RouterLink to="/login">Login</RouterLink><RouterLink to="/api-docs">API Docs</RouterLink><a href="#">Privacy</a><a href="#">Terms</a><a href="#">Security</a>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.footer { padding-top: clamp(44px, 5vw, 68px); border-top: 1px solid var(--line); margin-top: 0; position: relative; background: var(--bg-0); }
.footer-cta {
  display: flex; align-items: center; justify-content: space-between; gap: 30px; flex-wrap: wrap;
  padding: clamp(28px, 3.5vw, 44px);
  margin-bottom: clamp(36px, 4.5vw, 56px);
  background: linear-gradient(130deg, rgba(26,161,126,0.10), rgba(47,134,179,0.08));
  border-color: rgba(26,161,126,0.2);
}
/* .marketing-app .card h3 in main.css caps at 1.22rem — override it here */
.footer-cta h3 { font-size: clamp(1.5rem, 3vw, 2.1rem) !important; margin-bottom: 8px; font-weight: 700 !important; }
.footer-cta p { color: var(--ink-1); max-width: 52ch; }

.footer-grid { display: grid; grid-template-columns: 1.6fr repeat(3, 1fr); gap: 40px; padding-bottom: 36px; }
.footer .brand { display: inline-flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.footer .brand-name { font-family: var(--font-display); font-weight: 800; font-size: 1.2rem; color: var(--ink-0); }
.footer .brand-ai { background: var(--grad-primary); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.footer-tag { color: var(--ink-2); max-width: 38ch; font-size: 0.93rem; }
.badges { display: flex; gap: 10px; margin-top: 20px; flex-wrap: wrap; }
/* `.footer` prefix: bid_ui's Tailwind layer also defines a global `.badge`. */
.footer .badge { display: inline-flex; align-items: center; gap: 6px; font-size: 0.78rem; color: var(--ink-1); padding: 6px 11px; border: 1px solid var(--line); border-radius: 999px; background: var(--bg-2); }

.footer-col h4 { font-size: 0.82rem; text-transform: uppercase; letter-spacing: 0.1em; color: var(--ink-2); margin-bottom: 16px; }
.footer-col ul { display: grid; gap: 11px; }
.footer-col a { color: var(--ink-1); font-size: 0.93rem; transition: color 0.25s; }
.footer-col a:hover { color: var(--teal-bright); }

.footer-bottom { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; padding: 26px 0; border-top: 1px solid var(--line); }
.footer-bottom p { color: var(--ink-3); font-size: 0.86rem; }
.footer-legal { display: flex; gap: 22px; }
.footer-legal a { color: var(--ink-2); font-size: 0.86rem; transition: color 0.25s; }
.footer-legal a:hover { color: var(--teal-bright); }

@media (max-width: 860px) { .footer-grid { grid-template-columns: 1fr 1fr; } }
@media (max-width: 540px) { .footer-grid { grid-template-columns: 1fr; gap: 30px; } }
</style>
